<template>
    <div>
        <fullscreen-loading v-if="loading" />

        <div class="row mt-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-1" v-if="!embed">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="h4">{{ $t('designer.corporate_style') }}</span>
                        </div>
                    </div>
                    <div class="card-body">
                        <span class="h5 mb-4">{{ $t('designer.corporate_style_settings') }}</span>
                        <div class="row mb-4">
                            <div class="col-md-6">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label" for="logo">{{ $t('designer.company_logo') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('logo')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <div class="d-flex flex-column align-items-center justify-content-between h-75">
                                    <div class="logo-preview mb-1 mt-3">
                                        <CompanyImage v-if="!logo" :type="CompanyImageType.Logo" class="img-fluid" height="80px" />
                                        <img v-else :src="logo" class="img-fluid" style="height: 80px">
                                    </div>
                                    <input id="logo" type="file" ref="logoInput" class="d-none" accept="image/*" @change="handleFileChange($event, 'logo')">
                                    <button type="button" class="btn btn-primary btn-sm" @click="$refs.logoInput.click()">
                                        {{ $t('designer.change_logo') }}
                                    </button>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label" for="favicon">{{ $t('designer.favicon') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('favicon')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <div class="d-flex flex-column align-items-center justify-content-between h-75">
                                    <div class="favicon-preview mb-1 mt-3" style="width: 32px; height: 32px;">
                                        <CompanyImage v-if="!favicon" :type="CompanyImageType.Favicon" class="img-fluid" />
                                        <img v-else :src="favicon" class="img-fluid" alt="Favicon">
                                    </div>
                                    <input type="file" id="favicon" ref="faviconInput" class="d-none" accept="image/*" @change="handleFileChange($event, 'favicon')">
                                    <button type="button" class="btn btn-primary btn-sm" @click="$refs.faviconInput.click()">
                                        {{ $t('designer.change_favicon') }}
                                    </button>
                                </div>
                            </div>

                            <div class="col-md-6 mt-4">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label" for="primary_color">{{ $t('designer.primary_color') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('primary_color')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text">
                                            <icon class="paint-brush" name="paint-brush"></icon>
                                        </span>
                                    </div>
                                    <input type="color" id="primary_color" class="form-control" v-model="primary_color" @change="saveChanges">
                                    <input type="text" class="form-control" v-model="primary_color">
                                </div>

                                <small>{{ $t('designer.corporate_style_primary_color_description') }}</small>
                            </div>

                            <div class="col-md-6 mt-4">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label" for="manual_pdf">{{ $t('designer.manual_pdf') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('manual_pdf')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <div class="d-flex flex-column align-items-center justify-content-between h-75">
                                    <div class="manual-pdf-preview mb-2">
                                        <a :href="manual_pdf" target="_blank">{{ $t('designer.uploaded_pdf') }}</a>
                                    </div>
                                    <input id="manual_pdf" type="file" ref="manualPdfInput" class="d-none" accept="application/pdf" @change="handleFileChange($event, 'manual_pdf')">
                                    <button type="button" class="btn btn-primary btn-sm" @click="$refs.manualPdfInput.click()">
                                        {{ $t('designer.change_manual_pdf') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <span class="h5 mb-4">{{ $t('designer.corporate_general_settings') }}</span>
                        <div class="row mb-4">
                            <div class="col-md-6 mt-4">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label normal-cursor">{{ $t('designer.submit_info') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('submit_info')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <Editor
                                    v-model="submit_info"
                                    name="submit_info"
                                ></Editor>
                            </div>
                            <div class="col-md-6 mt-4">
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <label class="form-control-label normal-cursor">{{ $t('designer.manual_info') }}</label>
                                        <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('manual_info')" v-tooltip="$t('designer.reset_style')">
                                            <icon name="retweet" scale="1.3" class="m-0"></icon>
                                        </button>
                                    </div>
                                </div>
                                <Editor
                                    v-model="manual_info"
                                    name="manual_info"
                                ></Editor>
                            </div>
                        </div>

                        <span class="h5 mb-4">{{ $t('designer.corporate_email_settings') }}</span>
                        <div class="row mb-4">
                            <div class="col-md-6 mt-4">
                                <div class="row">
                                    <div class="col-md-12">
                                        <div class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <label class="form-control-label" for="mail_from_name">{{ $t('designer.mail_from_name') }}</label>
                                                <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('mail_from_name')" v-tooltip="$t('designer.reset_style')">
                                                    <icon name="retweet" scale="1.3" class="m-0"></icon>
                                                </button>
                                            </div>
                                        </div>
                                        <input id="mail_from_name" type="email" class="form-control" v-model="mail_from_name" :disabled="loading" :class="{ 'is-invalid': errors?.mail_from_name?.length }">
                                        <span v-if="errors?.mail_from_name?.length" class="invalid-feedback">{{ errors.mail_from_name[0] }}</span>
                                    </div>
                                    <div class="col-md-12 mt-4">
                                        <div class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <label class="form-control-label" for="reply_to_name">
                                                    {{ $t('designer.reply_to_name') }}
                                                    <icon name="question-circle" scale="1.3" class="m-0" v-tooltip="$t('designer.reply_to_name_tooltip')"></icon>
                                                </label>
                                                <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('reply_to_name')" v-tooltip="$t('designer.reset_style')">
                                                    <icon name="retweet" scale="1.3" class="m-0"></icon>
                                                </button>
                                            </div>
                                        </div>
                                        <input id="reply_to_name" type="text" class="form-control" v-model="reply_to_name" :disabled="loading" :class="{ 'is-invalid': errors?.reply_to_name?.length }">
                                        <span v-if="errors?.reply_to_name?.length" class="invalid-feedback">{{ errors.reply_to_name[0] }}</span>
                                    </div>
                                    <div class="col-md-12 mt-4">
                                        <div class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <label class="form-control-label" for="reply_to_email">
                                                    {{ $t('designer.reply_to_email') }}
                                                    <icon name="question-circle" scale="1.3" class="m-0" v-tooltip="$t('designer.reply_to_email_tooltip')"></icon>
                                                </label>
                                                <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('reply_to_email')" v-tooltip="$t('designer.reset_style')">
                                                    <icon name="retweet" scale="1.3" class="m-0"></icon>
                                                </button>
                                            </div>
                                        </div>
                                        <input id="reply_to_email" type="email" class="form-control" v-model="reply_to_email" :disabled="loading" :class="{ 'is-invalid': errors?.reply_to_email?.length }">
                                        <span v-if="errors?.reply_to_email?.length" class="invalid-feedback">{{ errors.reply_to_email[0] }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6 mt-4">
                                <div class="row">
                                    <div class="col-md-12">
                                        <div class="mb-2">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <label class="form-control-label normal-cursor">{{ $t('designer.email_layout') }}</label>
                                                <button type="button" class="btn btn-secondary btn-sm btn-icon" @click="reset('email_layout')" v-tooltip="$t('designer.reset_style')">
                                                    <icon name="retweet" scale="1.3" class="m-0"></icon>
                                                </button>
                                            </div>

                                            <small>{{ $t('emailtemplate.email_layout_description') }}</small>
                                        </div>

                                        <Editor
                                            :merge-fields="layoutMergeFields"
                                            v-model="email_layout"
                                            name="email_layout"
                                            @change="emailLayoutChanged = true"
                                        />

                                        <div v-if="errormessage" class="mb-2">
                                            <span class="text text-danger">{{ errormessage }}</span>
                                        </div>

                                        <small class="d-block mt-2">{{ $t('emailtemplate.mergefield_description') }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-12">
                                <div class="d-flex justify-content-end">
                                    <button type="button" class="btn btn-primary" @click="saveChanges" :disabled="loading">
                                        <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                        {{ $t('general.save') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, inject } from 'vue';
    import FullscreenLoading from '../../components/FullscreenLoading';
    import Editor from '../components/Editor.vue';
    import { notifyError, notifySuccess } from '../../utils/notify';
    import i18n from '../../utils/locale';
    import CompanyImage from '../../components/CompanyImage.vue';
    import { CompanyImageType } from '../../enums/company-image-type';

    const http = inject('http');
    const store = inject('store');

    const layoutMergeFields = [
        {
            name: 'INHOUD',
            required: true,
        },
        {
            name: 'BEDRIJF-NAAM',
        },
        {
            name: 'BEDRIJF-REPLY-TO-NAAM',
        },
        {
            name: 'BEDRIJF-REPLY-TO-EMAIL',
        },
        {
            name: 'BEDRIJF-LOGO',
        },
    ];

    const errors = ref(null);

    const loading = ref(false);
    const logo = ref(null);
    const favicon = ref(null);
    const primary_color = ref('#007bff');
    const manual_info = ref('');
    const submit_info = ref('');
    const manual_pdf = ref(null);
    const mail_from_name = ref(null);
    const reply_to_name = ref(null);
    const reply_to_email = ref(null);
    const email_layout = ref('');
    const emailLayoutChanged = ref(false);
    const errormessage = ref(null);

    const logoInput = ref(null);
    const faviconInput = ref(null);
    const manualPdfInput = ref(null);

    const embed = computed(() => {
        return window.location.search.includes('embed=true');
    });

    onMounted(() => {
        load();
    });

    async function load() {
        loading.value = true;
        try {
            const response = await http.get('/api/company/style');
            const data = response.data;

            primary_color.value = data.primary_color;
            manual_info.value = data.manual_info ?? '';
            submit_info.value = data.submit_info ?? '';
            mail_from_name.value = data.mail_from_name;
            reply_to_name.value = data.reply_to_name;
            reply_to_email.value = data.reply_to_email;
            email_layout.value = data.email_layout;
            manual_pdf.value = data.manual_pdf;
        } catch (exception) {
            notifyError(exception, i18n.t('designer.failed_to_load_corporate_style'));
        } finally {
            loading.value = false;
        }
    }

    function handleFileChange(event, refType) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = e => {
            if (refType === 'logo') {
                logo.value = e.target.result;
            } else if (refType === 'favicon') {
                favicon.value = e.target.result;
            } else if (refType === 'manual_pdf') {
                manual_pdf.value = e.target.result;
            }
        };
        reader.readAsDataURL(file);

        saveChanges();
    }

    async function saveChanges() {
        loading.value = true;
        errormessage.value = null;
        errors.value = null;

        try {
            const formData = new FormData();
            const logoFile = logoInput.value.files[0];
            const faviconFile = faviconInput.value.files[0];
            const pdfFile = manualPdfInput.value.files[0];

            if (logoFile) {
                formData.append('logo', logoFile);
            }

            if (faviconFile) {
                formData.append('favicon', faviconFile);
            }

            if (pdfFile) {
                formData.append('manual_pdf', pdfFile);
            }

            formData.append('primary_color', primary_color.value);
            formData.append('submit_info', submit_info.value);
            formData.append('manual_info', manual_info.value);
            formData.append('mail_from_name', mail_from_name.value);

            if (reply_to_name.value?.length) {
                formData.append('reply_to_name', reply_to_name.value);
            }

            if (reply_to_email.value?.length) {
                formData.append('reply_to_email', reply_to_email.value);
            }

            if (emailLayoutChanged.value) {
                if (!email_layout.value || !email_layout.value.includes('[[INHOUD]]')) {
                    errormessage.value = i18n.t('designer.email_layout_mergefield_required');
                    notifyError(errormessage.value, errormessage.value);
                    loading.value = false;
                    return;
                }

                formData.append('email_layout', email_layout.value);
            }

            await http.post('/api/company/style', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            await load();
            await store.dispatch('reloadCompanySettings');

            notifySuccess(i18n.t('designer.corporate_style_updated'));
            errormessage.value = null;
            emailLayoutChanged.value = false;
        } catch (exception) {
            errors.value = exception?.response?.data?.errors || null;
            notifyError(exception, i18n.t('designer.failed_to_update_corporate_style'));
        } finally {
            loading.value = false;
        }
    }

    async function reset(type) {
        try {
            await http.post('/api/company/style/reset', {
                type,
            });

            await load();
            await store.dispatch('reloadCompanySettings');
            emailLayoutChanged.value = false;

            notifySuccess(i18n.t('designer.corporate_style_updated'));
        } catch (exception) {
            if (exception?.response.status === 422) {
                // Show the validation error message
                errormessage.value = exception.response.data.message || i18n.t('designer.validation_error');
            }

            notifyError(exception, i18n.t('designer.failed_to_reset_corporate_style'));
        }
    }
</script>


<style scoped>
    .btn-sm {
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
        border-radius: 0.2rem;
    }
</style>
